import React, { useEffect, useState } from "react";
import { Modal, Form, Button, Divider, message } from "antd";
import { SyncOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { AddUserModalProps, UserFormValues } from "./UserInterfaces";
import { fetchDashboards, addUserAPI } from "src/services/UserManagementAPIs";
import StyledTextInput from "src/components/reusable/StyledTextInput";
import StyledDropdown from "src/components/reusable/StyledDropdown";

const AddUserModal: React.FC<AddUserModalProps> = ({
  isOpen,
  onClose,
  addUser,
  tenant_id,
  api_key,
  isSocialListeningEnabled,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const selectedRole = Form.useWatch("role", form);
  const [selectedDashboards, setSelectedDashboards] = useState<string[]>([]);
  const [selectedFeatures, setSelectedFeatures] = useState<string[]>([]);
  const [dashboardOptions, setDashboardOptions] = useState<
    { name: string; value: string }[]
  >([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isAddingUser, setIsAddingUser] = useState(false);

  const featureOptions = [
    {
      label: t("userManagement.modals.common.socialListeningAgent"),
      value: "social listening agent",
    },
  ];

  useEffect(() => {
    if (isOpen && dashboardOptions.length === 0) {
      loadDashboards();
    }
  }, [isOpen]);

  const loadDashboards = async () => {
    setIsFetching(true);
    try {
      const dashboards = await fetchDashboards(tenant_id, api_key);
      const normalized = dashboards.map(dashboard => ({
        name: dashboard.name,
        value: dashboard.value,
      }));
      const withAll = [
        { name: t("userManagement.modals.addUser.allDashboards"), value: "all" },
        ...normalized,
      ];
      setDashboardOptions(withAll);
    } catch {
      message.error(t("userManagement.modals.addUser.fetchDashboardsFailed"));
    } finally {
      setIsFetching(false);
    }
  };

  const handleDashboardChange = (value: string[]) => {
    const allValue = "all";

    const actualDashboardValues = dashboardOptions
      .filter(opt => opt.value !== allValue)
      .map(opt => opt.value);

    const hadAllSelected = selectedDashboards.includes(allValue);
    const hasAllSelected = value.includes(allValue);

    let nextValue: string[];

    if (hasAllSelected && !hadAllSelected) {
      // User just checked "All Dashboards"
      nextValue = [allValue, ...actualDashboardValues];
    } else if (hadAllSelected && !hasAllSelected) {
      // User removed the "All Dashboards" tag itself: clear the whole selection
      nextValue = [];
    } else {
      // Removing/adding an individual dashboard tag
      const filtered = value.filter(v => v !== allValue);
      const hasSelectedAll =
        actualDashboardValues.length > 0 &&
        filtered.length === actualDashboardValues.length;

      nextValue = hasSelectedAll ? [allValue, ...actualDashboardValues] : filtered;
    }

    setSelectedDashboards(nextValue);
    form.setFieldsValue({ dashboardAccess: nextValue });
  };

  const handleSubmit = async (formValues: UserFormValues) => {
    setIsAddingUser(true);
    try {
      const dashboardsToSend = selectedDashboards.filter(d => d !== "all");
      const socialListeningAccess = selectedFeatures.includes(
        "social listening agent"
      );

      const newUser = await addUserAPI(
        formValues,
        dashboardsToSend,
        socialListeningAccess,
        tenant_id,
        api_key
      );
      addUser(newUser);
      message.success(t("userManagement.modals.addUser.addSuccess"));
      form.resetFields();
      setSelectedDashboards([]);
      setSelectedFeatures([]);
      onClose();
    } catch (error: any) {
      const backendErrorMessage =
        error?.response?.data?.detail ||
        error?.response?.data?.message ||
        error?.message ||
        t("userManagement.modals.addUser.addFailedFallback");

      const cleanErrorMessage = Array.isArray(backendErrorMessage)
        ? backendErrorMessage.map(e => e.msg).join(", ")
        : String(backendErrorMessage).replace(/^\d+:\s*/, "").trim();

      message.error(cleanErrorMessage);
    } finally {
      setIsAddingUser(false);
    }
  };

  return (
    <Modal
      title={t("userManagement.modals.addUser.title")}
      open={isOpen}
      onCancel={onClose}
      footer={null}
      width={600}
      className="add-user-modal"
    >
      <Divider />
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        className="add-user-form"
        initialValues={{ role: "viewer", features: [] }}
      >
        <div className="form-row">
          <Form.Item
            name="firstName"
            label={t("userManagement.modals.common.firstNameLabel")}
            className="form-item error-message"
            rules={[
              {
                required: true,
                message: t("userManagement.modals.common.firstNameRequired"),
              },
              {
                pattern: /^[A-Za-z\s]+$/,
                message: t("userManagement.modals.common.firstNameAlpha"),
              },
            ]}
          >
            <StyledTextInput
              placeholder={t("userManagement.modals.common.firstNamePlaceholder")}
            />
          </Form.Item>

          <Form.Item
            name="lastName"
            label={t("userManagement.modals.common.lastNameLabel")}
            className="form-item error-message"
            rules={[
              {
                required: true,
                message: t("userManagement.modals.common.lastNameRequired"),
              },
              {
                pattern: /^[A-Za-z\s]+$/,
                message: t("userManagement.modals.common.lastNameAlpha"),
              },
            ]}
          >
            <StyledTextInput
              placeholder={t("userManagement.modals.common.lastNamePlaceholder")}
            />
          </Form.Item>
        </div>

        <Form.Item
          name="username"
          label={t("userManagement.modals.common.usernameLabel")}
          className="form-item error-message"
          rules={[
            {
              required: true,
              message: t("userManagement.modals.common.usernameRequired"),
            },
            {
              pattern: /^[a-zA-Z0-9.]{8,}$/,
              message: t("userManagement.modals.common.usernameRule"),
            },
          ]}
        >
          <StyledTextInput
            placeholder={t("userManagement.modals.common.usernamePlaceholder")}
          />
        </Form.Item>

        <Form.Item
          name="email"
          label={t("userManagement.modals.common.emailLabel")}
          className="form-item error-message"
          rules={[
            {
              validator: (_, value) => {
                if (!value) {
                  return Promise.reject(
                    new Error(t("userManagement.modals.common.emailRequired"))
                  );
                }
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(value)) {
                  return Promise.reject(
                    new Error(t("userManagement.modals.common.emailInvalid"))
                  );
                }
                return Promise.resolve();
              },
            },
          ]}
        >
          <StyledTextInput
            placeholder={t("userManagement.modals.common.emailPlaceholder")}
          />
        </Form.Item>

        <Form.Item
          name="role"
          label={t("userManagement.modals.common.roleLabel")}
          className="form-item error-message"
          rules={[
            {
              required: true,
              message: t("userManagement.modals.common.roleRequired"),
            },
          ]}
        >
          <StyledDropdown
            placeholder={t("userManagement.modals.common.rolePlaceholder")}
            value={selectedRole}
            options={[{ label: t("userManagement.role.viewer"), value: "viewer" }]}
            popupClassName="custom-dropdown-menu"
          />
        </Form.Item>

        {isSocialListeningEnabled && (
          <Form.Item
            name="features"
            label={t("userManagement.modals.common.featureAccessLabel")}
            className="form-item error-message"
          >
            <StyledDropdown
              mode="multiple"
              placeholder={t("userManagement.modals.common.featurePlaceholder")}
              value={selectedFeatures}
              onChange={value => setSelectedFeatures(value as string[])}
              options={featureOptions}
              showSearch={false}
              maxTagCount={2}
              className="filter-dropdown"
              popupClassName="filter-dropdown-menu"
            />
          </Form.Item>
        )}

        <Form.Item
          name="dashboardAccess"
          label={t("userManagement.modals.common.dashboardAccessLabel")}
          className="form-item error-message"
          rules={[
            {
              required: true,
              message: t("userManagement.modals.common.dashboardRequired"),
            },
          ]}
        >
          <StyledDropdown
            mode="multiple"
            placeholder={t("userManagement.modals.common.dashboardPlaceholder")}
            value={selectedDashboards}
            onChange={value => handleDashboardChange(value as string[])}
            options={dashboardOptions.map(option => ({
              label: option.name,
              value: option.value,
            }))}
            showSearch={false}
            maxTagCount={2}
            className="filter-dropdown"
            popupClassName="filter-dropdown-menu"
            disabled={isFetching}
          />
        </Form.Item>

        <Form.Item
          name="sendInvitation"
          valuePropName="checked"
          className="form-item-invitation"
        >
          <span>{t("userManagement.modals.addUser.invitationNote")}</span>
        </Form.Item>

        <Form.Item className="form-actions">
          <Button
            type="primary"
            htmlType="submit"
            className="button button--primary"
            disabled={isAddingUser}
            icon={isAddingUser ? <SyncOutlined spin /> : null}
          >
            {isAddingUser
              ? t("userManagement.modals.addUser.addingUser")
              : t("userManagement.modals.addUser.addUser")}
          </Button>
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default AddUserModal;
